/**
 * collections.ts — Typed immutable collection helpers
 *
 * Drop-in replacement for `immutable-functions` with full TypeScript support.
 * All functions return new arrays/objects — never mutate the input.
 *
 * Uses `structuredClone()` for deep copies where available (Node 17+, all modern browsers),
 * with a JSON-based fallback for older environments.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Deep clone — uses structuredClone when available, JSON fallback otherwise. */
const deepClone = <T>(obj: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(obj)
    : JSON.parse(JSON.stringify(obj));

/** Any object with a numeric or string `id` field. */
export interface Identifiable {
  id: number | string;
}

// ---------------------------------------------------------------------------
// Core helpers (operate on `id` by default)
// ---------------------------------------------------------------------------

/** Check whether an item with the given `id` exists in the array. */
export function exists<T extends Identifiable>(state: T[] | undefined, id: number | string | null | undefined): boolean {
  if (!state) return false;
  return state.some(x => x.id === id);
}

/** Return the index of the item with the given `id`, or `-1`. */
export function index<T extends Identifiable>(state: T[] | undefined, id: number | string | null | undefined): number {
  if (!state) return -1;
  return state.findIndex(x => x.id === id);
}

/** Find a single item by `id`. Returns `undefined` if not found. */
export function find<T extends Identifiable>(state: T[] | undefined, id: number | string | null | undefined): T | undefined {
  if (!state) return undefined;
  return state.find(x => x.id === id);
}

/**
 * Append `data` to the array if no item with the same `id` exists.
 * Returns the original array unchanged if the id already exists.
 */
export function create<T extends Identifiable>(state: T[] | undefined, data: T): T[] {
  const s = state || [] as T[];
  if (exists(s, data.id)) return s;
  return [...s, data];
}

/**
 * Immutably update the item matching `id` by shallow-merging `data`.
 * If the id doesn't exist, appends `data` instead (upsert).
 */
export function update<T extends Identifiable>(
  state: T[] | undefined,
  id: number | string | null | undefined,
  data: Partial<T>,
): T[] {
  const s = state || [];
  const idx = index(s, id);
  if (idx === -1) return [...s, data as T];
  const updated = [...s];
  updated[idx] = { ...updated[idx], ...data };
  return updated;
}

/** Remove the item with the given `id`. */
export function destroy<T extends Identifiable>(state: T[] | undefined, id: number | string | null | undefined): T[] {
  if (!state) return [];
  return state.filter(x => x.id !== id);
}

// ---------------------------------------------------------------------------
// ByProp variants (operate on an arbitrary key)
// ---------------------------------------------------------------------------

export function existsByProp<T>(state: T[] | undefined, prop: keyof T & string, val: unknown): boolean {
  if (!state) return false;
  return state.some(x => x[prop] === val);
}

export function indexByProp<T>(state: T[] | undefined, prop: keyof T & string, val: unknown): number {
  if (!state) return -1;
  return state.findIndex(x => x[prop] === val);
}

export function findByProp<T>(state: T[] | undefined, prop: keyof T & string, val: unknown): T | undefined {
  if (!state) return undefined;
  return state.find(x => x[prop] === val);
}

export function createByProp<T>(state: T[] | undefined, prop: keyof T & string, data: T): T[] {
  const s = state || [] as T[];
  if (existsByProp(s, prop, data[prop])) return s;
  return [...s, data];
}

export function updateByProp<T>(
  state: T[] | undefined,
  prop: keyof T & string,
  val: unknown,
  data: Partial<T>,
): T[] {
  const s = state || [];
  const idx = indexByProp(s, prop, val);
  if (idx === -1) return [...s, data as T];
  const updated = [...s];
  updated[idx] = { ...updated[idx], ...data };
  return updated;
}

export function destroyByProp<T>(state: T[] | undefined, prop: keyof T & string, val: unknown): T[] {
  if (!state) return [];
  return state.filter(x => x[prop] !== val);
}

// ---------------------------------------------------------------------------
// Merge — the "unique" function
// ---------------------------------------------------------------------------

/**
 * Deep-merge objects, recursively merging nested objects and upserting arrays by `id`.
 *
 * When `deep` is true, nested objects are recursively merged and
 * `structuredClone()` is used to avoid shared references.
 *
 * Behavior:
 * - Arrays:  upsert items by `id` (or by `prop` if specified)
 * - Objects: shallow-merge keys (or deep-merge when `deep=true`)
 * - Primitives / null: return `newState`
 */
export function merge<T>(state: T, newState: T, deep?: boolean): T;
export function merge<T>(state: T[], newState: T[], deep?: boolean): T[];
export function merge(state: unknown, newState: unknown, deep = false): unknown {
  if (newState == null) return state;

  if (Array.isArray(state)) {
    return arrayMerge(state, newState as unknown[], 'id', deep);
  }

  if (typeof state === 'object' && state !== null) {
    return objectMerge(
      state as Record<string, unknown>,
      newState as Record<string, unknown>,
      'id',
      deep,
    );
  }

  return newState;
}

export function mergeByProp<T>(
  state: T,
  newState: T,
  prop: string,
  deep = false,
): T {
  if (newState == null) return state;

  if (Array.isArray(state)) {
    return arrayMerge(state, newState as unknown[], prop, deep) as T;
  }

  if (typeof state === 'object' && state !== null) {
    return objectMerge(
      state as Record<string, unknown>,
      newState as Record<string, unknown>,
      prop,
      deep,
    ) as T;
  }

  return newState;
}

// ---------------------------------------------------------------------------
// Internal merge helpers
// ---------------------------------------------------------------------------

function objectMerge(
  state: Record<string, unknown>,
  obj: Record<string, unknown>,
  prop: string,
  deep: boolean,
): Record<string, unknown> {
  const result = deep ? deepClone(state) : { ...state };

  for (const key of Object.keys(obj)) {
    const val = obj[key];

    if (Array.isArray(val)) {
      result[key] = arrayMerge(
        (result[key] as unknown[]) || [],
        val,
        prop,
        deep,
      );
    } else if (deep && val && typeof val === 'object' && !Array.isArray(val)) {
      result[key] = objectMerge(
        (result[key] as Record<string, unknown>) || {},
        val as Record<string, unknown>,
        prop,
        deep,
      );
    } else {
      result[key] = val;
    }
  }

  return result;
}

function arrayMerge(
  state: unknown[],
  incoming: unknown[],
  prop: string,
  deep: boolean,
): unknown[] {
  if (!incoming) return state;
  let result = [...state];

  for (const item of incoming) {
    const record = item as Record<string, unknown>;
    const key = record[prop];

    const idx = result.findIndex(x => (x as Record<string, unknown>)[prop] === key);
    if (idx !== -1) {
      const existing = result[idx] as Record<string, unknown>;
      if (deep) {
        result[idx] = objectMerge(existing, record, prop, deep);
      } else {
        result[idx] = { ...existing, ...record };
      }
    } else {
      result = [...result, item];
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Wrap a Redux dispatch in a resolved Promise.
 * @deprecated — prefer async thunks or direct dispatch.
 */
export function promiseDispatch(obj: unknown) {
  return (dispatch: (action: unknown) => void): Promise<void> =>
    new Promise((resolve) => {
      dispatch(obj);
      resolve();
    });
}

/**
 * Dev helper: log which props changed between renders.
 * Only useful in development — strip from production.
 */
export function whatChanged(
  props: Record<string, unknown>,
  newProps: Record<string, unknown>,
  label = 'Props changed',
): void {
  console.group(label);
  for (const key of Object.keys(newProps)) {
    if (props[key] !== newProps[key]) {
      console.log(key, 'old val:', props[key], 'new val:', newProps[key]);
    }
  }
  console.groupEnd();
}
